import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowRight, Check, ListTodo, Plus, Timer } from 'lucide-react';
import { Link } from 'react-router-dom';
import { localDateKey, PomodoroTimer, pomodorosDone } from './Pomodoro';
import './FocusPanel.css';

const STORAGE_KEY = 'planner.focus-task';

function savedSelection() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return saved?.date === localDateKey() ? saved.id : null;
  } catch {
    return null;
  }
}

// Fit the complete title into the main area, including long Chinese titles.
function FocusTitle({ children }) {
  const container = useRef(null);
  const title = useRef(null);
  useLayoutEffect(() => {
    const fit = () => {
      if (!container.current || !title.current) return;
      const box = container.current;
      const heading = title.current;
      let size = Math.min(160, Math.max(64, box.clientWidth * 0.105));
      heading.style.fontSize = `${size}px`;
      while ((heading.scrollHeight > box.clientHeight || heading.scrollWidth > box.clientWidth) && size > 24) {
        size -= 2;
        heading.style.fontSize = `${size}px`;
      }
    };
    fit();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (observer) observer.observe(container.current);
    window.addEventListener('resize', fit);
    document.fonts?.ready.then(fit);
    return () => { observer?.disconnect(); window.removeEventListener('resize', fit); };
  }, [children]);
  return <div className="focus-title-area" ref={container}><h1 ref={title}>{children}</h1></div>;
}

export default function FocusPanel({ todos, loading, loadError, onRetry, onComplete, onPomodoroComplete, onAdd, onView }) {
  const [selectedId, setSelectedId] = useState(savedSelection);
  const [busy, setBusy] = useState(false);
  const saving = useRef(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [timerTaskId, setTimerTaskId] = useState(null);
  const current = todos.find(todo => todo.id === selectedId) || todos[0];
  const upcoming = todos.filter(todo => todo.id !== current?.id);
  const currentId = current?.id;

  useEffect(() => {
    if (loading || loadError) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ date: localDateKey(), id: currentId ?? null }));
    } catch { /* Focus remains usable when browser storage is unavailable. */ }
  }, [currentId, loading, loadError]);

  const select = (todo) => {
    setTimerTaskId(null);
    setSelectedId(todo.id);
    setError('');
    setNotice('');
  };

  const complete = async () => {
    if (!current || saving.current) return;
    saving.current = true;
    setBusy(true);
    setError('');
    try {
      await onComplete(current);
      setTimerTaskId(null);
      setSelectedId(upcoming[0]?.id ?? null);
      setNotice(`Completed: ${current.title}`);
    } catch {
      setError('Could not complete this task. Please try again.');
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };

  const savePomodoro = async () => {
    try {
      await onPomodoroComplete(current);
      setNotice('Pomodoro saved. Keep going, or mark the task complete.');
    } catch {
      setError('Could not save this pomodoro. Your task is still here.');
    }
  };

  return (
    <div className="focus-panel">
      <section className="focus-main" aria-label="Current focus">
        <header className="focus-header">
          <Link to="/" className="focus-brand">Samuel’s <span>2026</span></Link>
          <nav aria-label="Focus navigation">
            <button onClick={() => onView('today')}><ListTodo size={16} /><span>All tasks</span></button>
            <button onClick={() => onView('history')}>History</button>
            <button onClick={onAdd} aria-label="Add task"><Plus size={18} /></button>
          </nav>
        </header>

        <div className="focus-work">
          <div className="focus-eyebrow"><span className="focus-dot" />{loading ? 'GETTING READY' : current ? 'NOW · FOCUS ON ONE THING' : 'A LITTLE ROOM TO BREATHE'}</div>
          {loading ? <FocusTitle>Loading your day…</FocusTitle> : loadError ? <>
            <FocusTitle>Let’s try again.</FocusTitle>
            <p className="focus-error" role="alert">{loadError} <button onClick={onRetry}>Retry</button></p>
          </> : current ? <>
            <FocusTitle>{current.title}</FocusTitle>
            <div className="focus-task-meta">
              {current.scheduledDate && <span>Carried over · {current.scheduledDate}</span>}
              {current.estimated_minutes > 0 && <span>{current.estimated_minutes} min estimated</span>}
              {current.type === 'recurring' && <span>Recurring task</span>}
            </div>
          </> : <>
            <FocusTitle>Nothing left.<br />Enjoy your day.</FocusTitle>
            <p className="focus-empty-copy">Your task list is clear. Add a task whenever you’re ready.</p>
          </>}
        </div>

        <footer className="focus-actions">
          <div className="focus-feedback">
            {error ? <span role="alert" className="focus-error">{error}</span> : <span role="status">{notice || 'One thing at a time.'}</span>}
          </div>
          {!loading && !loadError && (current ? <div className="focus-controls">
            {timerTaskId === current.id ? <PomodoroTimer
              key={current.id} compact task={current} minutes={25}
              index={pomodorosDone(current)} total={Math.max(1, Math.ceil((current.estimated_minutes || 25) / 25))}
              onComplete={savePomodoro} onClose={() => setTimerTaskId(null)}
            /> : <button className="focus-timer-start" disabled={busy} onClick={() => { setError(''); setTimerTaskId(current.id); }}><Timer size={17} />25 min</button>}
            <button className="focus-complete" disabled={busy} onClick={complete}><Check size={18} />{busy ? 'Saving…' : 'Done · Next'}</button>
          </div> : <button className="focus-complete" onClick={onAdd}><Plus size={18} />Add a task</button>)}
        </footer>
      </section>

      <section className="focus-next" aria-labelledby="focus-next-label">
        <div className="focus-next-heading"><h2 id="focus-next-label">Up next <span>{loading || loadError ? '—' : upcoming.length}</span></h2><span>{upcoming.length > 0 ? 'Choose what comes next' : 'No rush.'}</span></div>
        <div className="focus-queue">
          {!loading && !loadError && upcoming.map((todo, index) => <button className="focus-queue-item" key={todo.id} disabled={busy} onClick={() => select(todo)} aria-label={`Focus on ${todo.title}`}>
            <span className="focus-queue-number">{String(index + 1).padStart(2, '0')}</span>
            <span className="focus-queue-copy"><strong>{todo.title}</strong><small>{todo.scheduledDate ? `Carried over · ${todo.scheduledDate}` : todo.estimated_minutes > 0 ? `${todo.estimated_minutes} min` : 'Ready when you are'}</small></span>
            <ArrowRight size={17} />
          </button>)}
          {!loading && !loadError && upcoming.length === 0 && <p className="focus-queue-empty">{current ? 'Just this one. The rest can wait.' : 'A clear list, a clear mind.'}</p>}
        </div>
      </section>
    </div>
  );
}
